import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { FileText, PenTool, Check, ArrowRight, Undo2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import Navbar from "../components/Navbar";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const ContractSign = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const [contract, setContract] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    fetchContract();
  }, [contractId]);

  const fetchContract = async () => {
    try {
      // contractId here is actually booking_id
      const bookingRes = await axios.get(`${API}/bookings/${contractId}`, { withCredentials: true });
      setBooking(bookingRes.data);
      
      if (bookingRes.data.contract) {
        setContract(bookingRes.data.contract);
      }
    } catch (error) {
      console.error("Error fetching contract:", error);
      toast.error("Contract not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    
    ctx.beginPath();
    ctx.moveTo(
      (e.clientX || e.touches[0].clientX) - rect.left,
      (e.clientY || e.touches[0].clientY) - rect.top
    );
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    
    ctx.lineTo(
      (e.clientX || e.touches[0].clientX) - rect.left,
      (e.clientY || e.touches[0].clientY) - rect.top
    );
    ctx.strokeStyle = "#D4AF37";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSign = async () => {
    if (!hasSignature) {
      toast.error("Please draw your signature");
      return;
    }

    setSigning(true);
    
    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL("image/png");
      
      await axios.post(`${API}/contracts/${contract.contract_id}/sign`, {
        signature_data: signatureData
      }, { withCredentials: true });
      
      toast.success("Contract signed successfully!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Error signing contract:", error);
      toast.error(error.response?.data?.detail || "Failed to sign contract");
    } finally {
      setSigning(false);
    }
  };

  // Check if user already signed
  const userIsCustomer = contract?.customer_id === user?.user_id;
  const alreadySigned = userIsCustomer 
    ? contract?.customer_signature 
    : contract?.vendor_signature;

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      <div className="pt-28 pb-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-gold" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-white">
                  Entertainment Services Contract
                </h1>
                <p className="text-white/60">
                  Review and sign the contract below
                </p>
              </div>
            </div>
            
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Contract Content */}
              <div className="lg:col-span-2 bg-charcoal rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5 bg-obsidian">
                  <h3 className="font-medium text-white">Contract Terms</h3>
                </div>
                <ScrollArea className="h-[500px] p-6">
                  <pre className="text-white/80 whitespace-pre-wrap font-sans text-sm leading-relaxed">
                    {contract?.terms}
                  </pre>
                </ScrollArea>
              </div>
              
              {/* Signature Panel */}
              <div className="bg-charcoal rounded-2xl border border-white/5 p-6">
                <h3 className="font-heading text-lg font-bold text-white mb-4">
                  Your Signature
                </h3>
                
                {alreadySigned ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-500" />
                    </div>
                    <p className="text-white font-medium">You've signed this contract</p>
                    <p className="text-white/60 text-sm mt-1">
                      {userIsCustomer ? (
                        contract?.vendor_signature 
                          ? "Waiting for payment" 
                          : "Waiting for vendor signature"
                      ) : (
                        contract?.customer_signature
                          ? "Contract fully executed"
                          : "Waiting for customer signature"
                      )}
                    </p>
                    <Button
                      onClick={() => navigate("/dashboard")}
                      className="mt-6 bg-gold text-black hover:bg-gold-light rounded-full"
                      data-testid="go-to-dashboard-btn"
                    >
                      Go to Dashboard
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <p className="text-white/60 text-sm mb-4">
                      Draw your signature in the box below
                    </p>
                    
                    <div className="relative">
                      <canvas
                        ref={canvasRef}
                        width={280}
                        height={150}
                        className="signature-canvas w-full"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        data-testid="signature-canvas"
                      />
                      <div className="absolute bottom-2 left-2 right-2 border-t border-gold/30"></div>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        onClick={clearSignature}
                        className="flex-1 border-white/10 text-white hover:bg-white/5 rounded-full"
                        data-testid="clear-signature-btn"
                      >
                        <Undo2 className="w-4 h-4 mr-2" />
                        Clear
                      </Button>
                      <Button
                        onClick={handleSign}
                        disabled={!hasSignature || signing}
                        className="flex-1 bg-gold text-black hover:bg-gold-light rounded-full font-bold"
                        data-testid="sign-contract-btn"
                      >
                        {signing ? (
                          <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <>
                            <PenTool className="w-4 h-4 mr-2" />
                            Sign
                          </>
                        )}
                      </Button>
                    </div>
                    
                    <p className="text-white/40 text-xs mt-4 text-center">
                      By signing, you agree to the terms outlined in this contract.
                    </p>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContractSign;
